import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tile = ({ label, value }) => (
  <div className="bg-white border border-[#E7E7E2] rounded-2xl p-6">
    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/45">
      {label}
    </div>
    <div className="mt-3 font-sora text-[34px] font-extrabold leading-none tracking-tight text-[#111111]">
      {value}
    </div>
  </div>
);

const BarList = ({ title, rows, nameKey }) => {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="bg-white border border-[#E7E7E2] rounded-2xl p-6">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/45">
        {title}
      </div>
      <ul className="mt-4 space-y-3">
        {rows.length === 0 && (
          <li className="font-manrope text-[13px] text-[#111111]/50">
            No data yet — it starts counting from today.
          </li>
        )}
        {rows.map((r) => (
          <li key={r[nameKey]}>
            <div className="flex items-baseline justify-between gap-3 font-manrope text-[13px]">
              <span className="text-[#111111] truncate font-mono text-[12px]">
                {r[nameKey]}
              </span>
              <span className="text-[#111111]/55 shrink-0">{r.views}</span>
            </div>
            <div className="mt-1 h-1 bg-[#F7F7F4] rounded overflow-hidden">
              <div
                className="h-full bg-[#2563EB]"
                style={{ width: `${(r.views / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AnalyticsPanel = () => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, [getToken]);

  if (!data) return null;

  return (
    <section data-testid="analytics-panel" className="mt-16">
      <div className="overline">— Analytics</div>
      <h2 className="mt-4 font-sora text-[28px] md:text-[36px] font-bold tracking-tight text-[#111111]">
        Private, first-party numbers.
      </h2>
      <p className="mt-3 font-manrope text-[14px] text-[#111111]/60 max-w-xl">
        No cookies, no IPs stored, no third-party scripts. Pageviews only —
        path, referrer domain, day. Admin pages excluded.
      </p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Tile label="Views today" value={data.today} />
        <Tile label="Last 7 days" value={data.week} />
        <Tile label={`Last ${data.days} days`} value={data.total} />
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <BarList title="Top pages" rows={data.top_paths} nameKey="path" />
        <BarList title="Top referrers" rows={data.top_refs} nameKey="ref" />
      </div>
    </section>
  );
};
